import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import SearchableSelect from "../../components/reports/SearchableSelect";
const input =
  "min-w-0 max-w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-primary-700 focus:ring-2 focus:ring-primary-100";
const button =
  "rounded-lg border px-3 py-2 text-sm font-medium transition hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-700 disabled:opacity-40";
const copy = (v) => JSON.parse(JSON.stringify(v));
const blank = (source) => ({
  source,
  columns: [],
  groupBy: [],
  filters: { mode: "AND", conditions: [] },
  sort: { column: 0, direction: "asc" },
  limit: 10000,
});
function Filters({ node, onChange, fields, depth = 0 }) {
  const group = node?.conditions !== undefined;
  if (group)
    return (
      <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap gap-2">
          <select
            aria-label="Filter group mode"
            className={input}
            value={node.mode}
            onChange={(e) => onChange({ ...node, mode: e.target.value })}
          >
            <option value="AND">Match all conditions</option>
            <option value="OR">Match any condition</option>
          </select>
          <button
            className={button}
            disabled={!fields.length}
            onClick={() =>
              onChange({
                ...node,
                conditions: [
                  ...node.conditions,
                  { field: fields[0]?.id, operator: "eq", value: "" },
                ],
              })
            }
          >
            Add filter
          </button>
          {depth < 3 && (
            <button
              className={button}
              onClick={() =>
                onChange({
                  ...node,
                  conditions: [
                    ...node.conditions,
                    { mode: "OR", conditions: [] },
                  ],
                })
              }
            >
              Add condition group
            </button>
          )}
        </div>
        {node.conditions.map((child, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Filters
                node={child}
                fields={fields}
                depth={depth + 1}
                onChange={(next) =>
                  onChange({
                    ...node,
                    conditions: node.conditions.map((c, j) =>
                      j === i ? next : c,
                    ),
                  })
                }
              />
            </div>
            <button
              aria-label="Remove condition or group"
              className={button}
              onClick={() =>
                onChange({
                  ...node,
                  conditions: node.conditions.filter((_, j) => j !== i),
                })
              }
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  const field = fields.find((f) => f.id === node.field);
  return (
    <div className="flex flex-wrap gap-2">
      <div className="min-w-[180px] max-w-full">
        <SearchableSelect
          options={fields.map((f) => ({ value: f.id, label: f.label }))}
          value={node.field || ""}
          placeholder="Choose a field"
          onChange={(value) =>
            onChange({ ...node, field: value, operator: "eq", value: "" })
          }
        />
      </div>
      <select
        aria-label="Filter operator"
        className={input}
        value={node.operator}
        onChange={(e) => onChange({ ...node, operator: e.target.value })}
      >
        {[
          "eq",
          "ne",
          "gt",
          "gte",
          "lt",
          "lte",
          ...(field?.type === "text" ? ["contains"] : []),
          "empty",
          "notEmpty",
        ].map((op) => (
          <option key={op} value={op}>
            {
              {
                eq: "Equals",
                ne: "Not equal",
                gt: "Greater than",
                gte: "At least",
                lt: "Less than",
                lte: "At most",
                contains: "Contains",
                empty: "Is empty",
                notEmpty: "Is not empty",
              }[op]
            }
          </option>
        ))}
      </select>
      {!["empty", "notEmpty"].includes(node.operator) && (
        <input
          aria-label="Filter value"
          className={input}
          type={
            field?.type === "number"
              ? "number"
              : field?.type === "date"
                ? "date"
                : "text"
          }
          value={node.value ?? ""}
          onChange={(e) => onChange({ ...node, value: e.target.value })}
        />
      )}
    </div>
  );
}
export default function CustomReportsPage() {
  const { accessToken, user } = useAuth(),
    can = (action) => hasPermission(user, "custom_reports." + action);
  const [catalogue, setCatalogue] = useState([]),
    [reports, setReports] = useState([]),
    [definition, setDefinition] = useState(blank("pos")),
    [name, setName] = useState(""),
    [shared, setShared] = useState(false),
    [selected, setSelected] = useState(null),
    [baseline, setBaseline] = useState(""),
    [search, setSearch] = useState(""),
    [reportSearch, setReportSearch] = useState(""),
    [favoritesOnly, setFavoritesOnly] = useState(false),
    [step, setStep] = useState(1),
    [advanced, setAdvanced] = useState(false),
    [editingDefaults, setEditingDefaults] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [result, setResult] = useState(null),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(100),
    [history, setHistory] = useState([]),
    [formula, setFormula] = useState({
      left: "",
      right: "",
      operator: "+",
      label: "Calculated value",
    });
  const request = (url, options = {}) =>
    apiRequest("/custom-reports" + url, { ...options, accessToken });
  const [runtimeFilters, setRuntimeFilters] = useState({
    mode: "AND",
    conditions: [],
  });
  const initialized = useRef(false);
  const load = async () => {
    const [sources, saved] = await Promise.all([
      request("/catalogue"),
      request(""),
    ]);
    setCatalogue(sources);
    setReports(saved);
    return sources;
  };
  useEffect(() => {
    let active = true;
    Promise.all([request("/catalogue"), request("")])
      .then(([sources, saved]) => {
        if (active) {
          setCatalogue(sources);
          setReports(saved);
          if (!initialized.current) {
            setDefinition(blank(sources[0]?.id || ""));
            initialized.current = true;
          }
        }
      })
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    }; /* credentials change when session refreshes */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);
  const source = catalogue.find((s) => s.id === definition.source),
    fields = source?.fields || [],
    numeric = fields.filter((f) => f.type === "number"),
    dirty = JSON.stringify({ name, shared, definition }) !== baseline,
    owned =
      !selected ||
      selected.ownerId === user?.id ||
      selected.ownerId === user?.sub;
  const change = (next) => {
    setDefinition(next);
    setResult(null);
    setError("");
  };
  const open = (r) => {
    setStep(3);
    setEditingDefaults(false);
    setAdvanced(false);
    setSelected(r);
    setName(r.name);
    setShared(r.isShared);
    setDefinition(copy(r.definition));
    setRuntimeFilters(
      copy(r.definition.filters || { mode: "AND", conditions: [] }),
    );
    setBaseline(
      JSON.stringify({
        name: r.name,
        shared: r.isShared,
        definition: r.definition,
      }),
    );
    setResult(null);
    setHistory([]);
    setError("");
  };
  const fresh = () => {
    setStep(1);
    setAdvanced(false);
    setEditingDefaults(false);
    setSelected(null);
    setName("");
    setShared(false);
    setDefinition(blank(catalogue[0]?.id || ""));
    setBaseline("");
    setResult(null);
    setHistory([]);
    setError("");
  };
  const act = async (task) => {
    setBusy(true);
    setError("");
    try {
      await task();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const save = () =>
    act(async () => {
      const preview = result;
      const saved = await request(selected ? "/" + selected.id : "", {
        method: selected ? "PATCH" : "POST",
        body: {
          name,
          isShared: shared,
          definition,
          version: selected?.version,
        },
      });
      const sources = await load();
      void sources;
      open({
        id: saved.id,
        version: saved.version,
        ownerId: user?.id || user?.sub,
        name,
        isShared: shared,
        definition: copy(definition),
      });
      setResult(preview);
    });
  const generate = () =>
    act(async () => {
      const data = await request(
        selected && !dirty ? "/" + selected.id + "/generate" : "/preview",
        {
          method: "POST",
          body:
            selected && !dirty ? { filters: runtimeFilters } : { definition },
        },
      );
      setResult(data);
      setStep(3);
      setPage(1);
    });
  const download = (format) =>
    act(async () => {
      const response = await request(
        "/" + selected.id + "/export?format=" + format,
        {
          method: "POST",
          body: { filters: runtimeFilters },
          rawResponse: true,
        },
      );
      const url = URL.createObjectURL(await response.blob()),
        a = document.createElement("a");
      a.href = url;
      a.download = name.replace(/[^\w-]+/g, "-") + "." + format;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  const move = (from, to) => {
    if (to < 0 || to >= definition.columns.length) return;
    const columns = [...definition.columns];
    columns.splice(to, 0, columns.splice(from, 1)[0]);
    change({ ...definition, columns, sort: { column: 0, direction: "asc" } });
  };

  const switchSource = (id) => {
    if (id === definition.source) return;
    if (
      definition.columns.length &&
      !window.confirm(
        "Changing the data source clears the selected columns and filters. Continue?",
      )
    )
      return;
    change(blank(id));
    setSearch("");
    setAdvanced(false);
  };
  const template = (summary) => {
    const measure = ["amount", "value", "quantity", "accepted"].find((id) =>
      fields.some((f) => f.id === id),
    );
    let columns,
      groupBy = [];
    if (summary) {
      columns = [
        { field: "brand", label: "Brand" },
        {
          field: measure,
          label: fields.find((f) => f.id === measure)?.label || "Total",
          aggregate: "sum",
        },
      ];
      groupBy = ["brand"];
    } else
      columns = [
        "date",
        "bill",
        "grn",
        "order",
        "invoice",
        "article",
        "product",
        "sku",
        "quantity",
        "accepted",
        "amount",
        "value",
      ]
        .filter((id) => fields.some((f) => f.id === id))
        .slice(0, 8)
        .map((id) => ({
          field: id,
          label: fields.find((f) => f.id === id).label,
        }));
    change({
      ...blank(definition.source),
      columns,
      groupBy,
      grandTotal: !!summary,
    });
    if (!name)
      setName(
        (source?.label || "Custom report") +
          (summary ? " by brand" : " details"),
      );
    setAdvanced(!!summary);
    setStep(2);
  };
  const nextStep = () => {
    if (step === 1 && !source) {
      setError("Choose a data source first.");
      return;
    }
    if (step === 2 && !definition.columns.length) {
      setError("Choose at least one column.");
      return;
    }
    setError("");
    setStep(Math.min(3, step + 1));
  };
  const saveChecked = () => {
    if (!name.trim()) {
      setStep(1);
      setError("Give your report a name first.");
      return;
    }
    if (!definition.columns.length) {
      setStep(2);
      setError("Choose at least one column.");
      return;
    }
    save();
  };
  const toggleField = (f) =>
    change({
      ...definition,
      columns:
        !advanced && definition.columns.some((c) => c.field === f.id)
          ? definition.columns.filter((c) => c.field !== f.id)
          : [...definition.columns, { field: f.id, label: f.label }],
      sort: { column: 0, direction: "asc" },
    });
  const favorites = reports
    .filter(
      (r) =>
        (!favoritesOnly || r.isFavorite) &&
        r.name.toLowerCase().includes(reportSearch.toLowerCase()),
    )
    .sort((a, b) => Number(!!b.isFavorite) - Number(!!a.isFavorite));
  const runClean = selected && !dirty && !editingDefaults;
  if (!can("view"))
    return <p>You do not have permission to view custom reports.</p>;
  return (
    <div className="space-y-5 text-slate-800">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-800">
            Business reports
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950">
            Custom Reports
          </h1>
        </div>
        <button
          className={button + " bg-primary-800 text-white"}
          disabled={!can("create") || busy}
          onClick={fresh}
        >
          + New report
        </button>
      </header>
      {error && (
        <p
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {error}
        </p>
      )}
      <div className="grid items-start gap-5 xl:grid-cols-[250px_minmax(0,1fr)]">
        <aside className="rounded-2xl border border-slate-200 bg-white p-4 xl:sticky xl:top-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold">Your saved reports</h2>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs">
              {reports.length}
            </span>
          </div>
          <input
            aria-label="Search saved reports"
            className={input + " w-full"}
            placeholder="Find a saved report…"
            value={reportSearch}
            onChange={(e) => setReportSearch(e.target.value)}
          />
          <label className="my-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={favoritesOnly}
              onChange={(e) => setFavoritesOnly(e.target.checked)}
            />
            Favourites only
          </label>
          <div className="max-h-[45vh] space-y-2 overflow-auto">
            {favorites.map((r) => (
              <div
                key={r.id}
                className={
                  "flex items-start rounded-xl border " +
                  (selected?.id === r.id
                    ? "border-primary-800 bg-primary-50"
                    : "border-slate-200")
                }
              >
                <button
                  className="min-w-0 flex-1 p-3 text-left"
                  onClick={() => open(r)}
                >
                  <span className="block truncate text-sm font-semibold">
                    {r.name}
                  </span>
                  <span className="mt-1 block text-xs text-slate-500">
                    {r.isShared ? "Shared" : "Private"} · Version {r.version}
                  </span>
                </button>
                <button
                  className="p-3 text-lg text-primary-800"
                  aria-label={
                    (r.isFavorite ? "Unfavourite " : "Favourite ") + r.name
                  }
                  disabled={busy}
                  onClick={() =>
                    act(async () => {
                      await request("/" + r.id + "/favorite", {
                        method: "POST",
                        body: { value: !r.isFavorite },
                      });
                      await load();
                    })
                  }
                >
                  {r.isFavorite ? "★" : "☆"}
                </button>
              </div>
            ))}
          </div>
          {!favorites.length && (
            <div className="rounded-xl border border-dashed p-5 text-center text-sm text-slate-500">
              {favoritesOnly
                ? "No favourites yet. Use the star beside a report."
                : "Your reports will appear here after saving."}
            </div>
          )}
        </aside>
        <main className="min-w-0 space-y-5">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold">
                  {selected ? name || "Saved report" : "Create your report"}
                </h2>
                <span
                  className={
                    "rounded-full px-3 py-1 text-xs " +
                    (selected && !dirty
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-amber-50 text-amber-800")
                  }
                >
                  {selected && !dirty
                    ? "Saved · ready to generate"
                    : "Unsaved draft"}
                </span>
              </div>
              <nav
                aria-label="Report builder steps"
                className="grid grid-cols-3 gap-2"
              >
                {[
                  ["Data", "Choose where to start"],
                  ["Columns", "Choose what to show"],
                  ["Preview", "Filter, save and export"],
                ].map(([title, hint], i) => (
                  <button
                    key={title}
                    aria-current={step === i + 1 ? "step" : undefined}
                    className={
                      "rounded-xl border p-3 text-left transition " +
                      (step === i + 1
                        ? "border-primary-800 bg-primary-50 text-primary-800"
                        : "border-slate-200 text-slate-500 hover:border-primary-300")
                    }
                    onClick={() => {
                      setStep(i + 1);
                      setError("");
                    }}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <span
                        className={
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs " +
                          (step === i + 1
                            ? "bg-primary-800 text-white"
                            : "bg-slate-100")
                        }
                      >
                        {i + 1}
                      </span>
                      {title}
                    </span>
                    <span className="mt-1 hidden text-xs sm:block">{hint}</span>
                  </button>
                ))}
              </nav>
            </div>
            <div className="space-y-5 p-5">
              {step === 1 && (
                <>
                  <label className="block text-sm font-medium">
                    Report name
                    <input
                      aria-label="Report name"
                      className={input + " mt-2 w-full"}
                      placeholder="For example, Monthly stock by brand"
                      value={name}
                      maxLength={150}
                      onChange={(e) => {
                        setName(e.target.value);
                        setResult(null);
                      }}
                    />
                  </label>
                  <div>
                    <h3 className="mb-3 font-semibold">
                      What would you like to report on?
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {catalogue.map((s) => (
                        <button
                          key={s.id}
                          aria-pressed={definition.source === s.id}
                          className={
                            "rounded-xl border p-4 text-left " +
                            (definition.source === s.id
                              ? "border-primary-800 bg-primary-50 ring-1 ring-primary-800"
                              : "border-slate-200 hover:border-primary-300")
                          }
                          onClick={() => switchSource(s.id)}
                        >
                          <span className="mb-2 block text-lg text-primary-800">
                            {definition.source === s.id ? "◉" : "○"}
                          </span>
                          <span className="block text-sm font-semibold">
                            {s.label}
                          </span>
                          <span className="mt-2 block text-xs text-slate-500">
                            {s.fields.length} available fields
                          </span>
                        </button>
                      ))}
                    </div>
                    {!catalogue.length && (
                      <p className="rounded-lg bg-slate-50 p-4 text-sm">
                        No data sources available. Check your source
                        permissions.
                      </p>
                    )}
                  </div>
                  {source && (
                    <div className="rounded-xl bg-slate-50 p-4">
                      <h3 className="mb-3 text-sm font-semibold">
                        Start with a layout
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        <button
                          className={button + " bg-white"}
                          onClick={() => template(false)}
                        >
                          Detailed list
                        </button>
                        {fields.some((f) => f.id === "brand") &&
                          fields.some((f) =>
                            [
                              "amount",
                              "value",
                              "quantity",
                              "accepted",
                            ].includes(f.id),
                          ) && (
                            <button
                              className={button + " bg-white"}
                              onClick={() => template(true)}
                            >
                              Totals by brand
                            </button>
                          )}
                        <button
                          className={button + " bg-white"}
                          onClick={() => setStep(2)}
                        >
                          Choose my own columns →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {step === 2 && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="font-semibold">Choose your columns</h3>
                      <span className="text-xs text-slate-500">
                        {definition.columns.length} selected · drag or use
                        arrows to reorder
                      </span>
                    </div>
                    <button
                      className={button}
                      aria-pressed={advanced}
                      onClick={() => setAdvanced(!advanced)}
                    >
                      {advanced ? "Hide advanced options" : "Advanced options"}
                    </button>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-[230px_minmax(0,1fr)]">
                    <div className="rounded-xl border border-slate-200 p-3">
                      <input
                        aria-label="Search fields"
                        className={input + " mb-3 w-full"}
                        placeholder="Find a field…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="max-h-96 space-y-1 overflow-auto">
                        {fields
                          .filter((f) =>
                            f.label
                              .toLowerCase()
                              .includes(search.toLowerCase()),
                          )
                          .map((f) => {
                            const checked = definition.columns.some(
                              (c) => c.field === f.id,
                            );
                            return (
                              <button
                                key={f.id}
                                aria-pressed={checked}
                                className={
                                  "flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm " +
                                  (checked
                                    ? "bg-primary-50 font-medium text-primary-800"
                                    : "hover:bg-slate-50")
                                }
                                onClick={() => toggleField(f)}
                              >
                                <span>{checked && !advanced ? "✓" : "+"}</span>
                                {f.label}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                    <div className="min-w-0 space-y-2">
                      {!definition.columns.length && (
                        <div className="flex min-h-48 items-center justify-center rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                          Select fields from the list to build your report.
                        </div>
                      )}
                      {definition.columns.map((col, i) => (
                        <div
                          key={i}
                          draggable
                          onDragStart={(e) =>
                            e.dataTransfer.setData("text/plain", String(i))
                          }
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const from = Number(
                              e.dataTransfer.getData("text/plain"),
                            );
                            if (Number.isInteger(from)) move(from, i);
                          }}
                          className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
                        >
                          <span className="text-sm font-semibold text-slate-400">
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <input
                              aria-label={"Column " + (i + 1) + " heading"}
                              className="w-full min-w-24 rounded border border-transparent bg-transparent p-1 text-sm font-medium hover:border-slate-300 focus:border-primary-700"
                              value={col.label}
                              onChange={(e) =>
                                change({
                                  ...definition,
                                  columns: definition.columns.map((c, j) =>
                                    i === j
                                      ? { ...c, label: e.target.value }
                                      : c,
                                  ),
                                })
                              }
                            />
                            <span className="block px-1 text-xs text-slate-400">
                              {col.formula
                                ? "Calculated column"
                                : fields.find((f) => f.id === col.field)?.label}
                            </span>
                          </div>
                          {(advanced || col.aggregate) && (
                            <select
                              aria-label={"Column " + (i + 1) + " total"}
                              className={input}
                              value={col.aggregate || ""}
                              onChange={(e) =>
                                change({
                                  ...definition,
                                  columns: definition.columns.map((c, j) =>
                                    i === j
                                      ? {
                                          ...c,
                                          aggregate:
                                            e.target.value || undefined,
                                        }
                                      : c,
                                  ),
                                })
                              }
                            >
                              {[
                                ["", "Show detail"],
                                ["sum", "Total"],
                                ["avg", "Average"],
                                ["min", "Smallest"],
                                ["max", "Largest"],
                                ["count", "Count"],
                                ["distinctCount", "Unique count"],
                              ].map(([v, l]) => (
                                <option
                                  key={v}
                                  value={v}
                                  disabled={
                                    ["sum", "avg"].includes(v) &&
                                    !col.formula &&
                                    fields.find((f) => f.id === col.field)
                                      ?.type !== "number"
                                  }
                                >
                                  {l}
                                </option>
                              ))}
                            </select>
                          )}
                          <div className="flex items-center gap-2">
                            <button
                              className="p-1 text-slate-500 disabled:opacity-30"
                              aria-label={"Move column " + (i + 1) + " up"}
                              disabled={i === 0}
                              onClick={() => move(i, i - 1)}
                            >
                              ↑
                            </button>
                            <button
                              className="p-1 text-slate-500 disabled:opacity-30"
                              aria-label={"Move column " + (i + 1) + " down"}
                              disabled={i === definition.columns.length - 1}
                              onClick={() => move(i, i + 1)}
                            >
                              ↓
                            </button>
                            <button
                              className="p-1 text-red-600"
                              aria-label={"Remove column " + (i + 1)}
                              onClick={() =>
                                change({
                                  ...definition,
                                  columns: definition.columns.filter(
                                    (_, j) => j !== i,
                                  ),
                                  sort: { column: 0, direction: "asc" },
                                })
                              }
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {advanced && (
                    <div className="space-y-4 rounded-xl border border-primary-100 bg-primary-50 p-4">
                      <h3 className="font-semibold">Advanced options</h3>
                      <div>
                        <h4 className="mb-2 text-sm font-medium">
                          Group results by
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          {fields.map((f) => (
                            <label key={f.id} className="text-sm">
                              <input
                                type="checkbox"
                                checked={
                                  definition.groupBy?.includes(f.id) || false
                                }
                                onChange={(e) =>
                                  change({
                                    ...definition,
                                    groupBy: e.target.checked
                                      ? [...(definition.groupBy || []), f.id]
                                      : (definition.groupBy || []).filter(
                                          (k) => k !== f.id,
                                        ),
                                  })
                                }
                              />{" "}
                              {f.label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <label className="flex gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!definition.grandTotal}
                          onChange={(e) =>
                            change({
                              ...definition,
                              grandTotal: e.target.checked,
                            })
                          }
                        />
                        Grand totals across all matching rows
                      </label>
                      <details className="rounded-lg border border-primary-100 bg-white p-3">
                        <summary className="cursor-pointer text-sm font-medium">
                          Add a calculated column
                        </summary>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <input
                            aria-label="Calculated heading"
                            className={input}
                            value={formula.label}
                            onChange={(e) =>
                              setFormula({ ...formula, label: e.target.value })
                            }
                          />
                          {["left", "operator", "right"].map((k) => (
                            <select
                              key={k}
                              aria-label={"Formula " + k}
                              className={input}
                              value={formula[k]}
                              onChange={(e) =>
                                setFormula({ ...formula, [k]: e.target.value })
                              }
                            >
                              {k === "operator" ? (
                                ["+", "-", "*", "/"].map((o) => (
                                  <option key={o}>{o}</option>
                                ))
                              ) : (
                                <>
                                  <option value="">Choose numeric field</option>
                                  {numeric.map((f) => (
                                    <option key={f.id} value={f.id}>
                                      {f.label}
                                    </option>
                                  ))}
                                </>
                              )}
                            </select>
                          ))}
                          <button
                            className={button}
                            disabled={!formula.left || !formula.right}
                            onClick={() =>
                              change({
                                ...definition,
                                columns: [
                                  ...definition.columns,
                                  {
                                    label: formula.label,
                                    formula: {
                                      left: formula.left,
                                      right: formula.right,
                                      operator: formula.operator,
                                    },
                                  },
                                ],
                              })
                            }
                          >
                            Add calculation
                          </button>
                        </div>
                      </details>
                    </div>
                  )}
                </>
              )}
              {step === 3 && (
                <>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-slate-100 px-3 py-1.5">
                      {source?.label}
                    </span>
                    <button
                      className="rounded-full bg-slate-100 px-3 py-1.5 hover:bg-primary-50"
                      onClick={() => setStep(2)}
                    >
                      {definition.columns.length} columns · Edit
                    </button>
                    <span className="rounded-full bg-slate-100 px-3 py-1.5">
                      {shared ? "Shared" : "Private"}
                    </span>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-semibold">
                        {runClean ? "Filters for this run" : "Report filters"}{" "}
                        <span className="text-sm font-normal text-slate-400">
                          (optional)
                        </span>
                      </h3>
                      {runClean && (
                        <button
                          className={button}
                          onClick={() => {
                            setRuntimeFilters(
                              copy(
                                definition.filters || {
                                  mode: "AND",
                                  conditions: [],
                                },
                              ),
                            );
                            setResult(null);
                          }}
                        >
                          Reset filters
                        </button>
                      )}
                    </div>
                    {runClean && (
                      <div className="mb-3 flex gap-2">
                        <button
                          className={button}
                          onClick={() => {
                            const to = new Date(Date.now() + 19800000)
                                .toISOString()
                                .slice(0, 10),
                              from = new Date(Date.parse(to) - 29 * 86400000)
                                .toISOString()
                                .slice(0, 10);
                            setRuntimeFilters({
                              mode: "AND",
                              conditions: [
                                { field: "date", operator: "gte", value: from },
                                { field: "date", operator: "lte", value: to },
                              ],
                            });
                            setResult(null);
                          }}
                        >
                          Last 30 days
                        </button>
                      </div>
                    )}
                    <Filters
                      node={
                        runClean
                          ? runtimeFilters
                          : definition.filters || {
                              mode: "AND",
                              conditions: [],
                            }
                      }
                      fields={fields}
                      onChange={(filters) => {
                        if (runClean) {
                          setRuntimeFilters(filters);
                          setResult(null);
                        } else change({ ...definition, filters });
                      }}
                    />
                  </div>
                  <details className="rounded-xl border border-slate-200 p-4">
                    <summary className="cursor-pointer text-sm font-medium">
                      More options · sorting, sharing and saved defaults
                    </summary>
                    <div className="mt-3 flex flex-wrap items-end gap-3">
                      <label className="text-sm">
                        Sort by
                        <select
                          aria-label="Sort column"
                          className={input + " mt-1 block"}
                          value={definition.sort?.column || 0}
                          onChange={(e) =>
                            change({
                              ...definition,
                              sort: {
                                ...definition.sort,
                                column: Number(e.target.value),
                              },
                            })
                          }
                        >
                          {definition.columns.map((c, i) => (
                            <option key={i} value={i}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <select
                        aria-label="Sort direction"
                        className={input}
                        value={definition.sort?.direction || "asc"}
                        onChange={(e) =>
                          change({
                            ...definition,
                            sort: {
                              ...definition.sort,
                              direction: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                      </select>
                      <label className="text-sm">
                        Maximum rows
                        <input
                          aria-label="Row limit"
                          className={input + " mt-1 block w-28"}
                          type="number"
                          min="1"
                          max="10000"
                          value={definition.limit}
                          onChange={(e) =>
                            change({
                              ...definition,
                              limit: Number(e.target.value),
                            })
                          }
                        />
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={shared}
                          disabled={!can("share")}
                          onChange={(e) => setShared(e.target.checked)}
                        />
                        Share with authorised users
                      </label>
                      {runClean && owned && can("edit") && (
                        <button
                          className={button}
                          onClick={() => setEditingDefaults(true)}
                        >
                          Edit saved default filters
                        </button>
                      )}
                    </div>
                  </details>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 p-4">
              <button
                className={button + " bg-white"}
                disabled={step === 1 || busy}
                onClick={() => setStep(step - 1)}
              >
                ← Back
              </button>
              <div className="flex flex-wrap gap-2">
                {step < 3 ? (
                  <button
                    className={button + " bg-primary-800 text-white"}
                    disabled={busy || !source}
                    onClick={nextStep}
                  >
                    {step === 1 ? "Choose columns →" : "Filters & preview →"}
                  </button>
                ) : (
                  <>
                    <button
                      className={button + " bg-white"}
                      disabled={
                        busy ||
                        !owned ||
                        !can(selected ? "edit" : "create") ||
                        (!!selected && !dirty)
                      }
                      onClick={saveChecked}
                    >
                      Save report
                    </button>
                    <button
                      className={button + " bg-primary-800 text-white"}
                      disabled={
                        busy || !definition.columns.length || !can("generate")
                      }
                      onClick={generate}
                    >
                      {busy
                        ? "Working…"
                        : selected && !dirty
                          ? "Generate"
                          : "Preview report"}
                    </button>
                    {["xlsx", "json"].map((format) => (
                      <button
                        key={format}
                        className={button + " bg-white"}
                        disabled={
                          busy ||
                          !result ||
                          !selected ||
                          dirty ||
                          !can("export")
                        }
                        onClick={() => download(format)}
                      >
                        Export {format === "xlsx" ? "Excel" : "JSON"}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </section>
          {step === 3 && selected && (
            <details className="rounded-xl border border-slate-200 bg-white p-4">
              <summary className="cursor-pointer text-sm font-medium">
                Manage saved report
              </summary>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  className={button}
                  disabled={busy || !can("create")}
                  onClick={() => {
                    setSelected(null);
                    setShared(false);
                    setName(name + " copy");
                    setBaseline("");
                    setResult(null);
                    setStep(1);
                  }}
                >
                  Duplicate
                </button>
                <button
                  className={button}
                  disabled={busy || !owned || !can("edit")}
                  onClick={() =>
                    act(async () =>
                      setHistory(await request("/" + selected.id + "/history")),
                    )
                  }
                >
                  Version history
                </button>
                <button
                  className={button + " text-red-700"}
                  disabled={busy || !owned || !can("delete")}
                  onClick={() => {
                    if (window.confirm("Delete this saved report?"))
                      act(async () => {
                        await request("/" + selected.id, { method: "DELETE" });
                        await load();
                        fresh();
                      });
                  }}
                >
                  Delete
                </button>
              </div>
              {history.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {history.map((v) => (
                    <button
                      key={v.version}
                      className={button}
                      onClick={() => {
                        setDefinition(copy(v.definition));
                        setName(v.name);
                        setShared(v.isShared);
                        setResult(null);
                        setHistory([]);
                        setStep(2);
                        setAdvanced(true);
                      }}
                    >
                      Load v{v.version} as draft ·{" "}
                      {new Date(v.createdAt).toLocaleString()}
                    </button>
                  ))}
                </div>
              )}
            </details>
          )}
          {result && step === 3 && (
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
                <div>
                  <h3 className="font-semibold">Report preview</h3>
                  <span className="text-xs text-slate-500">
                    {result.rows.length} rows ·{" "}
                    {new Date(result.generatedAt).toLocaleString()}
                    {result.truncated ? " · Row limit reached" : ""}
                  </span>
                </div>
                <label className="text-sm">
                  Rows per page{" "}
                  <select
                    className={input}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[100, 200, 300, 500, 1000].map((n) => (
                      <option key={n}>{n}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="max-h-[65vh] overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-primary-800 text-white">
                    <tr>
                      {result.columns.map((c) => (
                        <th
                          key={c.key}
                          className="whitespace-nowrap border border-primary-900 p-3 text-left"
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows
                      .slice((page - 1) * pageSize, page * pageSize)
                      .map((r, i) => (
                        <tr
                          key={i}
                          className="even:bg-slate-50 hover:bg-primary-50"
                        >
                          {result.columns.map((c) => (
                            <td
                              key={c.key}
                              className={
                                "whitespace-nowrap border border-slate-200 p-3 " +
                                (typeof r[c.key] === "number"
                                  ? "text-right tabular-nums"
                                  : "")
                              }
                            >
                              {typeof r[c.key] === "number"
                                ? r[c.key].toLocaleString("en-IN", {
                                    maximumFractionDigits: 4,
                                  })
                                : (r[c.key] ?? "—")}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                  {result.grandTotal && (
                    <tfoot className="bg-primary-50 font-semibold">
                      <tr>
                        <td
                          colSpan={result.columns.length}
                          className="border p-3"
                        >
                          Grand total · all matching rows
                        </td>
                      </tr>
                      <tr>
                        {result.columns.map((c) => (
                          <td
                            key={c.key}
                            className="border border-slate-200 p-3"
                          >
                            {typeof result.grandTotal[c.key] === "number"
                              ? result.grandTotal[c.key].toLocaleString(
                                  "en-IN",
                                  { maximumFractionDigits: 4 },
                                )
                              : (result.grandTotal[c.key] ?? "—")}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
                {!result.rows.length && (
                  <p className="p-8 text-center text-sm text-slate-500">
                    No matching records. Try changing your filters.
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 border-t p-3">
                <button
                  className={button}
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <span className="text-sm">
                  Page {page} /{" "}
                  {Math.max(1, Math.ceil(result.rows.length / pageSize))}
                </span>
                <button
                  className={button}
                  disabled={page * pageSize >= result.rows.length}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
